import { useEffect, useState, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { DefaultButton, PrimaryButton, Stack, Checkbox, Link, Text, Spinner, SpinnerSize } from '@fluentui/react';
import { useNavigate } from "react-router-dom";

import styles from "./Landing.module.css";

import Coeus from "../../assets/coeus.png";
import { handleError } from '../../toaster';
import { getToken } from '../../authConfig';
import { GetLandingInfoResponse, acceptTermsOfUse, getLandingInfoData } from "../../api";
import { UserContext } from "../../components/UserContext";

const Landing = () => {
    const client = useMsal().instance;
    const userContext = useContext(UserContext);
    const { userData, setShouldRefreshContext } = userContext;
    const [landingInfoData, setLandingInfoData] = useState<GetLandingInfoResponse>();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [rugRead, setRugRead] = useState(false);
    const [trainingCompleted, setTrainingCompleted] = useState(false);
    const navigate = useNavigate();

    const handleAccept = async () => {
        try {
            const token = client ? await getToken(client) : undefined;
            const success = await acceptTermsOfUse(token);
            if (success) {
                setTermsAccepted(true);
                setShouldRefreshContext(true);
            } else {
                console.error('Failed to accept terms');
            }
        } catch (error) {
            handleError(error, "Error accepting terms");
        }
    };

    const handleNotAccept = async () => {
        window.location.href = '/logout';
    };

    useEffect(() => {
        if (userData?.tou_accepted) {
            const preLandingUrl = sessionStorage.getItem('preLandingUrl'); // Retrieve preLandingUrl from storage
            sessionStorage.removeItem('preLandingUrl');  // Clean up storage
            navigate(preLandingUrl || '/');  // Navigate to preLandingUrl or home if not found
        }
    }, [userData, navigate]);

    useEffect(() => {
        async function init() {
            const token = client ? await getToken(client) : undefined;
            getLandingInfoData(token)
                .then((response: GetLandingInfoResponse) => {
                    setLandingInfoData(response);
                })
                .catch(err => handleError(err, "Failed to fetch LandingInfoData"));
        }
        init();
    }, []);

    return (
        <div className={styles.landingContainer}>
            <img src={Coeus}
                className={`${styles.chatLogo} ${userContext.useDarkMode ? styles.chatLogoDark : ""}`}
                alt="Coeus" />
            {landingInfoData ? (
                <Stack tokens={{ childrenGap: 20 }}>
                    <Text>
                        This service, "Coeus" (pronounced "Key-us"), is for authorised Queensland Treasury or the National Injury Insurance Agency, Queensland (NIIAQ) personnel, contractors and consultants only.
                        Unauthorised use of this service is not permitted. Use of the service is logged and may be monitored for management and audit review purposes.
                        Inappropriate ICT usage is a breach of the Code of Conduct and may lead to disciplinary action.
                    </Text>
                    <Text>
                        I understand that “Coeus” is a generative AI service and that I have additional obligations when using this service that are over and
                        above the terms and conditions set out in Treasury's <Link href={landingInfoData.ICT_POLICY_URL} target="_blank" rel="noopener noreferrer">Use of ICT facilities and devices policy</Link>.
                    </Text>
                    <Text>I have:</Text>
                    <Stack tokens={{ childrenGap: 10 }} verticalAlign="start">
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <Checkbox checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)} />
                            <Text>
                                read and agree to the <Link href={landingInfoData.TERMS_URL} target="_blank" rel="noopener noreferrer"> Coeus Terms and Conditions of Use</Link>
                            </Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <Checkbox checked={rugRead} onChange={() => setRugRead(!rugRead)} />
                            <Text>
                                read and understood the <Link href={landingInfoData.RUG_URL} target="_blank" rel="noopener noreferrer">Coeus Responsible Usage Guidelines</Link>
                            </Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <Checkbox checked={trainingCompleted} onChange={() => setTrainingCompleted(!trainingCompleted)} />
                            <Text>
                                watched the introductory training <Link href={landingInfoData.TRAINING_URL} target="_blank" rel="noopener noreferrer">videos</Link>
                            </Text>
                        </Stack>
                    </Stack>
                    <Stack.Item className={styles.buttonContainer}>
                        <PrimaryButton
                            className={styles.acceptButton}
                            disabled={!termsAccepted || !rugRead || !trainingCompleted}
                            onClick={handleAccept}>
                            Continue
                        </PrimaryButton>
                        <DefaultButton
                            className={styles.notAcceptButton}
                            onClick={handleNotAccept}>
                            Exit
                        </DefaultButton>
                    </Stack.Item>
                </Stack>
            ) : (
                <Spinner size={SpinnerSize.large} label="Loading Data..." ariaLive="assertive" />
            )}
        </div>
    );
};

export default Landing;